import type { UseQueryOptions } from '@tanstack/react-query';

import type {
  MetagameTokenPackageData,
  MetagameTokenPackageResponse,
  MetagamesUpcomingEvent,
  MetagamesHistoricalEvents,
} from '../../dto/metagames.types';
import { MetaGamesEventType } from '../../dto/metagames.types';
import { getRequest } from '../../services/api';
import { bingoQueryKeys } from '../../services/queryClient';
import type { ApiResponse } from '../../services/types';
import type { BingoFeaturedPrizesData, MetagameBingoGame, MetagameHistoricalBingoGame } from '../../stores/bingo/types';
import { useStores } from '../../stores/useStores';

import {
  FIVE_MINUTES,
  FIFTEEN_MINUTES,
  NO_STALE_TIME,
  ONE_HOUR,
  THIRTY_MINUTES,
  TWO_HOURS,
  SEVEN_SECONDS,
  ONE_MINUTE,
} from './constants';
import { useQuery } from './useCustomQuery';
import { combineEnabled, extractEnabled } from './utils';

export const UPCOMING_EVENTS_DAYS_LIMIT = 30;
export const HISTORICAL_EVENTS_DAYS_LIMIT = 30;

export const useBingoUpcomingEvents = (
  daysAhead: number = UPCOMING_EVENTS_DAYS_LIMIT,
  options?: Omit<UseQueryOptions<MetagamesUpcomingEvent<MetagameBingoGame>[]>, 'queryKey' | 'queryFn'>,
) => {
  const { bingoStore, metagamesStore } = useStores();
  const { enabled, restOptions } = extractEnabled(options);

  return useQuery({
    queryKey: bingoQueryKeys.upcomingEvents(daysAhead),
    queryFn: async () => {
      const upcomingEvents = await metagamesStore.fetchUpcomingEvents<MetagameBingoGame>(
        MetaGamesEventType.BINGO,
        daysAhead,
      );

      bingoStore.updateUpcomingEventsFromQuery(upcomingEvents);

      return upcomingEvents;
    },
    staleTime: THIRTY_MINUTES,
    gcTime: ONE_HOUR,
    enabled: combineEnabled(bingoStore.bingoEnabled, enabled),
    ...restOptions,
  });
};

export const useBingoHistoricalEvents = (
  lastNDays: number = HISTORICAL_EVENTS_DAYS_LIMIT,
  options?: Omit<UseQueryOptions<MetagamesHistoricalEvents<MetagameHistoricalBingoGame>[]>, 'queryKey' | 'queryFn'>,
) => {
  const { bingoStore, metagamesStore } = useStores();
  const { enabled, restOptions } = extractEnabled(options);

  return useQuery({
    queryKey: bingoQueryKeys.historicalEvents(lastNDays),
    queryFn: async () => {
      const historicalEvents = await metagamesStore.fetchHistoricalEvents<MetagameHistoricalBingoGame>(
        MetaGamesEventType.BINGO,
        lastNDays,
      );

      bingoStore.updateHistoricalEventsFromQuery(historicalEvents);

      return historicalEvents;
    },
    staleTime: ONE_HOUR,
    gcTime: TWO_HOURS,
    enabled: combineEnabled(bingoStore.bingoEnabled, enabled),
    ...restOptions,
  });
};

export const useBingoStorePackages = (
  options?: Omit<UseQueryOptions<MetagameTokenPackageData[]>, 'queryKey' | 'queryFn'>,
) => {
  const { bingoStore } = useStores();
  const { enabled, restOptions } = extractEnabled(options);

  return useQuery({
    queryKey: bingoQueryKeys.storePackages(),
    queryFn: async () => {
      const res =
        await getRequest<ApiResponse<MetagameTokenPackageData[], 'bingoStorePackages'>>('bingo/store-packages');
      const storePackages = res.data?.bingoStorePackages ?? [];

      bingoStore.updateBingoStorePackagesFromQuery(storePackages);

      return storePackages;
    },
    staleTime: FIFTEEN_MINUTES,
    enabled: combineEnabled(bingoStore.bingoEnabled, enabled),
    ...restOptions,
  });
};

export const useBingoFeaturedPrizes = (
  options?: Omit<UseQueryOptions<BingoFeaturedPrizesData>, 'queryKey' | 'queryFn'>,
) => {
  const { bingoStore } = useStores();
  const { enabled, restOptions } = extractEnabled(options);

  return useQuery({
    queryKey: bingoQueryKeys.featuredPrizes(),
    queryFn: async () => {
      const res = await getRequest<ApiResponse<BingoFeaturedPrizesData>>('bingo/featured-prizes');
      const featuredPrizes = res.data?.data ?? { pending: [], redeemed: [] };

      bingoStore.updateFeaturedPrizesFromQuery(featuredPrizes);

      return featuredPrizes;
    },
    staleTime: FIFTEEN_MINUTES,
    enabled: combineEnabled(bingoStore.bingoEnabled, enabled),
    ...restOptions,
  });
};

export const useBingoCurrentEvent = (
  options?: Omit<UseQueryOptions<null>, 'queryKey' | 'queryFn'> & {
    enablePolling?: boolean;
  },
) => {
  const { bingoStore, metagamesStore } = useStores();
  const { enablePolling, ...restOptions } = options || {};
  const { enabled, restOptions: queryOptions } = extractEnabled(restOptions);

  return useQuery({
    queryKey: bingoQueryKeys.currentEvent(),
    queryFn: async () => {
      await metagamesStore.fetch();
      return null;
    },
    staleTime: NO_STALE_TIME,
    gcTime: FIVE_MINUTES,
    refetchOnWindowFocus: true,
    refetchInterval: (_query: unknown) => {
      if (!enablePolling) {
        return false;
      }

      const currentEvent = bingoStore.currentBingoEvent;
      if (!currentEvent) {
        return false;
      }

      const eventStatus = bingoStore.getEventStatus(currentEvent);
      if (eventStatus === 'live') {
        return ONE_MINUTE;
      }
      if (eventStatus && ['closed', 'aboutToStart'].includes(eventStatus)) {
        return SEVEN_SECONDS;
      }

      return false;
    },
    enabled: combineEnabled(bingoStore.bingoEnabled, enabled),
    ...queryOptions,
  });
};

export const useBingoTokenPrice = (
  quantity: number,
  options?: Omit<UseQueryOptions<MetagameTokenPackageResponse>, 'queryKey' | 'queryFn'>,
) => {
  const { bingoStore, metagamesStore } = useStores();
  const { enabled, restOptions } = extractEnabled(options);

  return useQuery({
    queryKey: bingoQueryKeys.tokenPrice(quantity),
    queryFn: async (): Promise<MetagameTokenPackageResponse> => {
      return metagamesStore.getTokenPackagePrice(MetaGamesEventType.BINGO, quantity);
    },
    staleTime: ONE_MINUTE,
    enabled: combineEnabled(bingoStore.bingoEnabled, quantity > 0 && enabled),
    ...restOptions,
  });
};
